<template>
  <!--
    "More apps" flyout beside the collapsed rail (design 1b narrow + 1g "More"). A 264px panel the
    sidebar positions next to the rail. Its list is a vuedraggable list in the sidebar's group, so a
    row can be dragged onto the rail (and a rail tile dropped here). It never mutates `items`:
    a drag emits `update-list(ids)` then `drag-end`, and the sidebar turns that into ONE reorder.
    Escape closes. Outside clicks are the shell's job (it owns the one scrim).
  -->
  <div
    v-if="open"
    ref="panel"
    class="app-more-flyout"
    :class="{ 'app-more-flyout--touch': touch }"
    role="dialog"
    aria-label="More apps"
    tabindex="-1"
    data-test="app-shell-more-panel"
    @keydown.esc="$emit('close')"
  >
    <div class="app-more-flyout__title">More apps</div>
    <div class="app-more-flyout__hint">{{ hint }}</div>

    <draggable
      class="app-more-flyout__list"
      tag="div"
      data-test="app-nav-flyout-list"
      :value="ids"
      group="app-shell-nav"
      draggable=".app-nav-item"
      filter=".app-nav-item__pin"
      :prevent-on-filter="false"
      :disabled="!canEdit"
      :delay="touch ? 250 : 0"
      :delay-on-touch-only="true"
      :touch-start-threshold="5"
      :animation="150"
      ghost-class="app-nav-ghost"
      chosen-class="app-nav-chosen"
      @start="$emit('drag-start')"
      @input="$emit('update-list', $event)"
      @end="$emit('drag-end')"
    >
      <AppNavItem
        v-for="row in items"
        :key="row.id"
        :item="row"
        :active="row.id === activeId"
        :pinned="false"
        variant="wide"
        :editable="canEdit"
        @select="$emit('select', $event)"
        @pin-toggle="$emit('pin', $event)"
      />
      <template #footer>
        <div v-if="!items.length" class="app-more-flyout__empty">Every app is pinned.</div>
      </template>
    </draggable>

    <button
      v-if="showReset"
      type="button"
      class="app-more-flyout__reset"
      data-test="app-nav-reset"
      @click="$emit('reset')"
    >Reset to default</button>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import AppNavItem from './AppNavItem.vue';

export default {
  name: 'AppMoreFlyout',
  components: { draggable, AppNavItem },
  props: {
    /** Resolved app rows (the More list, in order). */
    items: { type: Array, default: () => [] },
    canEdit: { type: Boolean, default: false },
    open: { type: Boolean, default: false },
    /** Highlight the current app if it lives in More. */
    activeId: { type: String, default: null },
    /** Tablet: press-and-hold to drag, 44px rows and pin targets. */
    touch: { type: Boolean, default: false },
    /** Admin + arrangement differs from the default. */
    showReset: { type: Boolean, default: false },
  },
  computed: {
    ids() {
      return this.items.map(r => r.id);
    },
    hint() {
      if (!this.canEdit) return 'Your office admin arranges the sidebar.';
      return this.touch ? 'Tap the pin to add one to your rail.' : 'Drag one onto the rail, or use the pin.';
    },
  },
  watch: {
    open(now) {
      if (now) this.$nextTick(this.focusPanel);
    },
  },
  mounted() {
    if (this.open) this.focusPanel();
  },
  methods: {
    focusPanel() {
      const el = this.$refs.panel;
      if (el && typeof el.focus === 'function') el.focus({ preventScroll: true });
    },
  },
};
</script>

<style lang="scss" scoped>
@import './shell';

.app-more-flyout {
  position: absolute;
  left: $shell-sidebar-rail + 8px;
  top: $shell-bar-height;
  z-index: 2;
  width: 264px;
  max-height: calc(100vh - #{$shell-bar-height + 16px});
  overflow-y: auto;
  padding: 8px 8px 8px 12px;
  background: $fu-surface;
  border: 1px solid $fu-border;
  border-radius: 14px;
  box-shadow: $fu-shadow-lg;
  outline: none;
  text-align: left;
  @include fu-font-base;
}

.app-more-flyout__title {
  padding: 6px 10px 2px;
  font-size: 14px;
  font-weight: $fu-fw-bold;
  color: $fu-text;
}

.app-more-flyout__hint {
  padding: 0 10px 8px;
  font-size: 12.5px;
  color: $fu-neutral-500;
}

.app-more-flyout__list {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-height: 36px;
}

.app-more-flyout__empty {
  padding: 10px;
  font-size: 13px;
  color: $fu-neutral-500;
}

.app-more-flyout__reset {
  @include fu-link-btn;
  margin: 8px 10px 4px;
  font-size: 12.5px;
}

// Tablet: 44px rows, an always-visible 44px pin target (touch has no hover).
.app-more-flyout--touch {
  ::v-deep .app-nav-item--wide {
    height: $shell-touch-target;
    padding-right: 0;
  }
  ::v-deep .app-nav-item--wide .app-nav-item__pin {
    display: flex;
    width: $shell-touch-target;
    height: $shell-touch-target;
    border-radius: 10px;
  }
  ::v-deep .app-nav-item--wide .app-nav-item__meta {
    display: none;
  }
}
</style>
